import { value } from '@takeshape/util';
import isString from 'lodash/isString.js';
import set from 'lodash/set.js';
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
import { getInitialData as baseGetInitialData } from "../util/initial-data.js";
export function parseShopifyHostName(hostname) {
    const newHost = hostname.replace(/(^\w+:|^)\/\//, '');
    const matches = newHost.split('.');
    return matches ? matches[0] : hostname;
}
export const DEFAULT_SHOPIFY_API_VERSION = '2025-01';
export const apiVersionSchema = {
    title: 'API Version',
    type: 'string',
    pattern: '\\d{4}-\\d{2}'
};
export const apiVersionFormConfig = {
    widget: 'looseDropdown',
    warning: 'The API version you have selected is no longer supported by Shopify.',
    options: ['2025-01', '2024-10', '2024-07', '2024-04'].map((version) => ({
        label: version,
        value: version
    }))
};
export function getAdminUrl(shop, version = DEFAULT_SHOPIFY_API_VERSION) {
    return `https://${shop}.myshopify.com/admin/api/${version}/graphql.json`;
}
export function getStorefrontUrl(shop, version = DEFAULT_SHOPIFY_API_VERSION) {
    return `https://${shop}.myshopify.com/api/${version}/graphql.json`;
}
export function parseVersion(url) {
    const matches = /\/(\d{4}-\d{2})\/graphql\.json$/.exec(url);
    return matches ? matches[1] : DEFAULT_SHOPIFY_API_VERSION;
}
export function getInitialData(serviceConfig) {
    const initialData = baseGetInitialData(this, serviceConfig);
    initialData.shop &&= `https://${initialData.shop}.myshopify.com`;
    initialData.apiVersion ||=
        typeof serviceConfig?.options?.endpoint === 'string'
            ? parseVersion(serviceConfig?.options?.endpoint)
            : DEFAULT_SHOPIFY_API_VERSION;
    return initialData;
}
const formSchema = {
    type: 'object',
    properties: {
        id: {
            title: 'Slug',
            type: 'string',
            pattern: '[0-9a-z_-]+',
            default: 'shopify'
        },
        title: {
            title: 'Name',
            type: 'string',
            default: 'Shopify'
        },
        namespace: {
            title: 'Namespace',
            type: 'string',
            pattern: '^[A-Z][A-Za-z]+$',
            default: 'Shopify'
        },
        shop: {
            title: '.myshopify.com URL',
            type: 'string'
        },
        apiVersion: apiVersionSchema,
        authentication: {
            title: 'Authentication',
            type: 'object',
            properties: {
                adminApiAccessToken: {
                    title: 'Admin API Access Token',
                    type: 'string'
                },
                apiSecretKey: {
                    title: 'API Secret Key',
                    type: 'string'
                }
            },
            required: ['adminApiAccessToken', 'apiSecretKey']
        }
    },
    required: ['id', 'title', 'namespace', 'shop', 'apiVersion', 'authentication']
};
const serviceProvider = {
    name: 'shopify',
    title: 'Shopify Admin',
    description: 'Shopify is a commerce platform that allows anyone to easily sell online, at a retail location, and everywhere in between.',
    categories: ['ecommerce'],
    links: [
        { title: 'Homepage', url: 'https://www.shopify.com/' },
        { title: 'Docs', url: '/docs/services/providers/shopify' }
    ],
    serviceType: 'graphql',
    introspectionType: 'graphql',
    createId: (options) => (isString(options.shop) ? `${parseShopifyHostName(options.shop)}` : ''),
    checkQueryParams(queryParams) {
        if (!queryParams.shop) {
            throw new Error('Invalid OAuth2 query params');
        }
    },
    getServerAuthentication: (options) => ({
        type: 'oauth2',
        authorizationUrl: isString(options.shop)
            ? `https://${parseShopifyHostName(options.shop)}.myshopify.com/admin/oauth/authorize`
            : '',
        accessTokenUrl: isString(options.shop)
            ? `https://${parseShopifyHostName(options.shop)}.myshopify.com/admin/oauth/access_token`
            : '',
        clientId: 'shopifyApiKey',
        clientSecret: 'shopifyApiSecret',
        scope: 'read_analytics,write_assigned_fulfillment_orders,read_assigned_fulfillment_orders,write_customers,read_customers,write_discounts,read_discounts,write_draft_orders,read_draft_orders,write_files,read_files,write_fulfillments,read_fulfillments,read_gdpr_data_request,write_gift_cards,read_gift_cards,write_inventory,read_inventory,write_legal_policies,read_legal_policies,read_locations,write_marketing_events,read_marketing_events,write_merchant_managed_fulfillment_orders,read_merchant_managed_fulfillment_orders,read_online_store_navigation,write_online_store_pages,read_online_store_pages,write_order_edits,read_order_edits,write_orders,read_orders,write_payment_terms,read_payment_terms,write_price_rules,read_price_rules,write_product_listings,read_product_listings,write_products,read_products,write_reports,read_reports,write_resource_feedbacks,read_resource_feedbacks,write_script_tags,read_script_tags,write_shipping,read_shipping,write_locales,read_locales,write_markets,read_markets,read_shopify_payments_accounts,read_shopify_payments_bank_accounts,read_shopify_payments_disputes,read_shopify_payments_payouts,write_content,read_content,write_themes,read_themes,write_third_party_fulfillment_orders,read_third_party_fulfillment_orders,write_translations,read_translations',
        headerPrefix: '',
        audience: '',
        redirectUrl: isString(options.apiBaseUrl) ? `${options.apiBaseUrl}/oauth/shopify/redirect` : ''
    }),
    getFormSchema: value(formSchema),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'shop', 'apiVersion', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A pascal-case prefix that will be added to types in your GraphQL API.'
            },
            shop: {
                widget: 'singleLineText',
                instructions: 'ex: https://example-shop.myshopify.com from Shopify'
            },
            apiVersion: apiVersionFormConfig,
            authentication: {
                order: ['adminApiAccessToken', 'apiSecretKey'],
                properties: {
                    adminApiAccessToken: {
                        widget: 'sensitiveField'
                    },
                    apiSecretKey: {
                        widget: 'sensitiveField'
                    }
                }
            }
        }
    }),
    getInitialData,
    getConfig(data) {
        const formData = data;
        const config = {
            title: formData.title ?? 'Shopify',
            namespace: formData.namespace ?? 'Shopify',
            provider: 'shopify',
            serviceType: 'graphql',
            authenticationType: 'custom'
        };
        if (formData.apiVersion) {
            set(config, 'options.apiVersion', formData.apiVersion);
        }
        if (formData.shop) {
            set(config, 'options.shop', parseShopifyHostName(formData.shop));
            set(config, 'options.endpoint', getAdminUrl(parseShopifyHostName(formData.shop), formData.apiVersion));
        }
        return updateAuthentication(config, formData, { type: 'custom' });
    },
    getCustomAuthInfo(authentication) {
        return [[['headers', 'X-Shopify-Access-Token'], authentication.adminApiAccessToken]];
    },
    getGraphqlObjectTypes: () => ['product', 'productVariant', 'collection'],
    isCompleteConfig: hasEveryKey(['authentication', 'options.shop', 'options.endpoint']),
    getHealthCheck: getHealthCheck({ checkName: 'graphqlIntrospection' })
};
export default serviceProvider;
